import Vue from "vue";
import elementLocale from "element-ui/lib/locale";
import elementZh from "element-ui/lib/locale/lang/zh-CN";
import elementEn from "element-ui/lib/locale/lang/en";
import zh from "./zh-CN";
import en from "./en-US";
import phrasesEn from "./phrases-en";
import phrasesHelpEn from "./phrases-help-en";
import {
  LOCALE_EN,
  LOCALE_ZH,
  persistLocale,
  readStoredLocale,
  translate,
} from "./util";

const packs = {
  [LOCALE_ZH]: zh,
  [LOCALE_EN]: en,
};

const phraseMap = Object.assign({}, phrasesEn, phrasesHelpEn);

export const i18nState = Vue.observable({
  locale: readStoredLocale(),
});

function applyElementLocale(locale) {
  elementLocale.use(locale === LOCALE_EN ? elementEn : elementZh);
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute(
      "lang",
      locale === LOCALE_EN ? "en" : "zh-CN"
    );
  }
}

export function currentDict() {
  return packs[i18nState.locale] || zh;
}

export function t(path, fallback) {
  void i18nState.locale;
  return translate(currentDict(), path, fallback);
}

export function tt(text) {
  void i18nState.locale;
  if (text == null || text === "") {
    return text;
  }
  const s = String(text);
  if (i18nState.locale !== LOCALE_EN) {
    return s;
  }
  return phraseMap[s] || s;
}

export function displayLabel(text) {
  void i18nState.locale;
  const s = String(text || "");
  const m = /^新页面(\d+)$/.exec(s);
  if (m) {
    return i18nState.locale === LOCALE_EN ? "Page " + m[1] : s;
  }
  return tt(s);
}

export function menuLabel(url, fallback) {
  void i18nState.locale;
  if (!url) {
    return tt(fallback || "");
  }
  const dict = currentDict();
  const mapped = dict.menus && dict.menus[url];
  if (mapped) {
    return mapped;
  }
  return tt(fallback || url);
}

export function setLocale(locale) {
  const next = locale === LOCALE_EN ? LOCALE_EN : LOCALE_ZH;
  i18nState.locale = next;
  persistLocale(next);
  applyElementLocale(next);
}

export function initI18n(VueCtor) {
  applyElementLocale(i18nState.locale);
  VueCtor.prototype.$t = function (path, fallback) {
    return t(path, fallback);
  };
  VueCtor.prototype.$menuLabel = function (url, fallback) {
    return menuLabel(url, fallback);
  };
  VueCtor.prototype.$tt = function (text) {
    return tt(text);
  };
  VueCtor.prototype.$displayLabel = function (text) {
    return displayLabel(text);
  };
}

export function wrapElementMessages(VueCtor) {
  const proto = VueCtor.prototype;
  const origConfirm = proto.$confirm;
  if (typeof origConfirm === "function") {
    proto.$confirm = function (message, title, options) {
      let opts = options;
      let ttl = title;
      if (title && typeof title === "object") {
        opts = title;
        ttl = "";
      }
      const nextOpts = Object.assign({}, opts || {});
      if (nextOpts.confirmButtonText) {
        nextOpts.confirmButtonText = tt(nextOpts.confirmButtonText);
      }
      if (nextOpts.cancelButtonText) {
        nextOpts.cancelButtonText = tt(nextOpts.cancelButtonText);
      }
      const msg = typeof message === "string" ? tt(message) : message;
      const heading = ttl ? tt(ttl) : ttl;
      return origConfirm.call(this, msg, heading, nextOpts);
    };
  }
  const msg = proto.$message;
  if (msg && typeof msg === "function") {
    ["success", "error", "warning", "info"].forEach((type) => {
      const fn = msg[type];
      if (typeof fn !== "function") {
        return;
      }
      msg[type] = function (payload) {
        if (typeof payload === "string") {
          return fn.call(this, tt(payload));
        }
        if (payload && typeof payload === "object" && typeof payload.message === "string") {
          const next = Object.assign({}, payload, { message: tt(payload.message) });
          if (typeof payload.title === "string") {
            next.title = tt(payload.title);
          }
          return fn.call(this, next);
        }
        return fn.call(this, payload);
      };
    });
    const origMessageFn = msg;
    if (typeof origMessageFn === "function") {
      proto.$message = function (payload) {
        if (typeof payload === "string") {
          return origMessageFn.call(this, tt(payload));
        }
        if (payload && typeof payload === "object" && typeof payload.message === "string") {
          const next = Object.assign({}, payload, { message: tt(payload.message) });
          if (typeof payload.title === "string") {
            next.title = tt(payload.title);
          }
          return origMessageFn.call(this, next);
        }
        return origMessageFn.call(this, payload);
      };
      ["success", "error", "warning", "info"].forEach((type) => {
        proto.$message[type] = msg[type];
      });
    }
  }
  const notify = proto.$notify;
  if (typeof notify === "function") {
    proto.$notify = function (options) {
      if (options && typeof options === "object") {
        const next = Object.assign({}, options);
        if (typeof next.title === "string") {
          next.title = tt(next.title);
        }
        if (typeof next.message === "string") {
          next.message = tt(next.message);
        }
        return notify.call(this, next);
      }
      return notify.call(this, options);
    };
  }
}

let formItemValidatePatched = false;

/** Translate Element Form async-validator messages on display */
export function patchElementFormI18n() {
  if (formItemValidatePatched) {
    return;
  }
  try {
    const ElFormItem = require("element-ui/packages/form/src/form-item.vue").default;
    const origValidate = ElFormItem.methods && ElFormItem.methods.validate;
    if (!origValidate) {
      return;
    }
    ElFormItem.methods.validate = function (trigger, callback) {
      const userCb =
        typeof callback === "function"
          ? (message, invalidFields) => {
              if (message) {
                const translated = tt(message);
                this.validateMessage = translated;
                callback(translated, invalidFields);
              } else {
                callback(message, invalidFields);
              }
            }
          : callback;
      return origValidate.call(this, trigger, userCb);
    };
    formItemValidatePatched = true;
  } catch (e) {
    /* element-ui form-item unavailable */
  }
}

export { LOCALE_EN, LOCALE_ZH };
